/**
 * Shared formatting helpers for the UserAdminPanel and its tab sub-components.
 *
 * These are small pure functions extracted to avoid duplication across tabs.
 */

export const formatDuration = (secs: number): string => {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  const hours = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export const formatRelativeTime = (input: number | string | null): string => {
  if (input == null) return "Never";

  let ms: number;
  if (typeof input === "string") {
    ms = new Date(input).getTime();
    if (isNaN(ms)) return "Unknown";
  } else {
    ms = input < 1e12 ? input * 1000 : input;
  }

  const diff = Date.now() - ms;
  if (diff < 0) return "Just now";
  if (diff < 60_000) return "Just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 2592000_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { dateStyle: "medium" });
};

export const formatTimestamp = (input: number | string | null): string => {
  if (input == null) return "--";
  let ms: number;
  if (typeof input === "string") {
    ms = new Date(input).getTime();
    if (isNaN(ms)) return "--";
  } else {
    ms = input < 1e12 ? input * 1000 : input;
  }
  return new Date(ms).toLocaleDateString(undefined, { dateStyle: "medium" });
};

export const formatFullTimestamp = (input: number | string | null): string => {
  if (input == null) return "--";
  let ms: number;
  if (typeof input === "string") {
    ms = new Date(input).getTime();
    if (isNaN(ms)) return "--";
  } else {
    ms = input < 1e12 ? input * 1000 : input;
  }
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const formatBitrate = (bps: number | null): string => {
  if (!bps) return "--";
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${Math.round(bps / 1_000)} Kbps`;
  return `${bps} bps`;
};
